import { Pencil, Undo2 } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { expectedOnHand } from '../lib/count-item-summary'
import type { CountItemView } from '@/api/endpoints/inventory-count-sessions'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { formatStockDisplay } from '@/shared/lib/stock-quantity'

interface CountNotFoundRowProps {
  item: CountItemView
  /** Opens the article sheet: the pad, and Ignorieren. */
  onOpen: () => void
  /** Takes an applied decision back — resolved rows only. */
  onReopen: () => void
  highlighted: boolean
  rowRef: (el: HTMLDivElement | null) => void
}

/**
 * An article the count expected but never recorded.
 *
 * The three answers used to be three buttons in a 3-column grid, which on a 390 px phone is three
 * German words in 106 px each. They are one gesture now: the pencil opens the pad, and the number
 * decides — 0 means "wirklich nichts da" and settles the article as confirmed zero, anything else
 * records what was found and leaves a variance to confirm. That is what accepting 0 as a quantity
 * buys (CHG-2026-09-inventory-count-review-ux); Ignorieren lives in the sheet, where there is room
 * to say what it does.
 *
 * The section is called "Nicht erfasst", not "Nicht gefunden": nobody searched, the article simply
 * never reached the count.
 */
export function CountNotFoundRow({
  item,
  onOpen,
  onReopen,
  highlighted,
  rowRef,
}: CountNotFoundRowProps) {
  const { t } = useTranslation('inventory-count')
  const resolved = item.resolution !== 'PENDING'

  return (
    <div
      ref={rowRef}
      className={cn(
        'flex min-h-[68px] items-center gap-2.5 border-b border-border px-4 py-3 transition-shadow',
        highlighted && 'ring-2 ring-primary ring-inset',
        resolved && 'bg-muted/40',
      )}
    >
      <button
        type="button"
        onClick={resolved ? onReopen : onOpen}
        className="min-w-0 flex-1 text-left"
      >
        <span
          className={cn(
            'block truncate text-[15px] font-medium leading-5',
            resolved && 'text-muted-foreground',
          )}
        >
          {item.name}
        </span>
        <span className="block truncate text-xs leading-4 tabular-nums text-muted-foreground">
          {resolved
            ? t('review.resolvedAs', {
                resolution: t(`review.resolution.${item.resolution}`),
              })
            : t('review.notCountedDetail', {
                expected: formatStockDisplay(expectedOnHand(item)),
              })}
        </span>
        {!resolved && item.affectedOrders.length > 0 && (
          <span className="block truncate text-xs leading-4 tabular-nums text-warning">
            {t('review.affectedOrders', { count: item.affectedOrders.length })}
          </span>
        )}
      </button>

      {resolved ? (
        <Button variant="outline" className="h-11 shrink-0" onClick={onReopen}>
          <Undo2 className="h-4 w-4" />
          {t('review.change')}
        </Button>
      ) : (
        <Button
          variant="outline"
          size="icon-lg"
          aria-label={t('review.openItem', { name: item.name })}
          onClick={onOpen}
        >
          <Pencil className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
